'use client';

import React, { useState, useEffect, useMemo } from 'react';
import FilterBar from './FilterBar';
import CaseStudyCard from './CaseStudyCard';
import Icon from '@/components/ui/AppIcon';

interface Metric {
  label: string;
  value: string;
  icon: string;
}

interface CaseStudy {
  id: string;
  title: string;
  client: string;
  industry: string;
  companySize: string;
  serviceType: string;
  description: string;
  image: string;
  imageAlt: string;
  metrics: Metric[];
  tags: string[];
  testimonial: {
    quote: string;
    author: string;
    position: string;
  };
  videoUrl?: string;
  pdfUrl?: string;
}

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

const PortfolioInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedCompanySize, setSelectedCompanySize] = useState('all');
  const [selectedServiceType, setSelectedServiceType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [displayCount, setDisplayCount] = useState(6);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const caseStudies: CaseStudy[] = [
  {
    id: 'techcorp-automation',
    title: 'AI-Powered Lead Generation System Drives 340% ROI',
    client: 'TechCorp Solutions',
    industry: 'Technology & SaaS',
    companySize: 'Mid-Market ($10M-$50M)',
    serviceType: 'Marketing Automation',
    description: 'Implemented comprehensive AI-driven lead scoring and nurturing system that transformed TechCorp\'s sales pipeline, reducing qualification time by 70% while increasing conversion rates by 89%.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_183be303d-1766470449787.png",
    imageAlt: 'Business team analyzing data on large digital screen in modern office',
    metrics: [
    { label: 'ROI Increase', value: '340%', icon: 'ChartBarIcon' },
    { label: 'Lead Quality', value: '+89%', icon: 'ArrowTrendingUpIcon' },
    { label: 'Time Saved', value: '70%', icon: 'ClockIcon' }],

    tags: ['AI Automation', 'Lead Generation', 'CRM Integration'],
    testimonial: {
      quote: 'CIS Solutions transformed our entire sales process. The AI-powered lead scoring system they implemented has been a game-changer, allowing our team to focus on high-value prospects and close deals faster than ever before.',
      author: 'Michael Chen',
      position: 'VP of Sales, TechCorp Solutions',
    },
    videoUrl: 'https://example.com/video',
    pdfUrl: 'https://example.com/case-study.pdf'
  },
  {
    id: 'healthplus-engagement',
    title: 'Patient Engagement Platform Increases Retention by 67%',
    client: 'HealthPlus Medical Group',
    industry: 'Healthcare & Wellness',
    companySize: 'Enterprise ($50M+)',
    serviceType: 'Digital Transformation',
    description: 'Developed integrated patient engagement platform combining automated appointment reminders, telehealth capabilities, and personalized health content, resulting in dramatic improvements in patient satisfaction and retention.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_137d7a8f4-1764671742989.png",
    imageAlt: 'Medical professional using tablet in bright modern healthcare facility',
    metrics: [
    { label: 'Retention', value: '+67%', icon: 'UserGroupIcon' },
    { label: 'No-Shows', value: '-52%', icon: 'ArrowTrendingDownIcon' },
    { label: 'Satisfaction', value: '4.8/5', icon: 'StarIcon' }],

    tags: ['Healthcare Tech', 'Patient Engagement', 'Telehealth'],
    testimonial: {
      quote: 'The patient engagement platform has revolutionized how we interact with our patients. Appointment no-shows have dropped dramatically, and our patient satisfaction scores have never been higher.',
      author: 'Dr. Sarah Martinez',
      position: 'Chief Medical Officer, HealthPlus Medical Group',
    },
    pdfUrl: 'https://example.com/case-study.pdf'
  },
  {
    id: 'retailmax-ecommerce',
    title: 'E-Commerce Optimization Boosts Revenue by $3.2M Annually',
    client: 'RetailMax',
    industry: 'E-Commerce & Retail',
    companySize: 'Growth-Stage ($1M-$10M)',
    serviceType: 'Marketing Strategy',
    description: 'Complete e-commerce platform overhaul with AI-powered product recommendations, abandoned cart recovery system, and personalized email campaigns that dramatically increased conversion rates and customer lifetime value.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_122b22f9d-1764654516001.png",
    imageAlt: 'Modern retail warehouse with organized inventory and digital management systems',
    metrics: [
    { label: 'Revenue', value: '+$3.2M', icon: 'CurrencyDollarIcon' },
    { label: 'Cart Recovery', value: '45%', icon: 'ShoppingCartIcon' },
    { label: 'CLV', value: '+120%', icon: 'ArrowTrendingUpIcon' }],

    tags: ['E-Commerce', 'Conversion Optimization', 'Email Marketing'],
    testimonial: {
      quote: 'Working with CIS Solutions was the best investment we made. Their data-driven approach to e-commerce optimization delivered results beyond our expectations, and the ROI has been phenomenal.',
      author: 'Jennifer Thompson',
      position: 'CEO, RetailMax',
    },
    videoUrl: 'https://example.com/video',
    pdfUrl: 'https://example.com/case-study.pdf'
  },
  {
    id: 'financeone-automation',
    title: 'Compliance Automation Saves 1,200 Hours Annually',
    client: 'FinanceOne Advisory',
    industry: 'Financial Services',
    companySize: 'Mid-Market ($10M-$50M)',
    serviceType: 'AI Implementation',
    description: 'Implemented AI-powered compliance monitoring and reporting system that automated 85% of regulatory documentation processes while ensuring 100% accuracy and reducing audit preparation time by 80%.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1370e02cd-1766487882870.png",
    imageAlt: 'Financial analyst reviewing compliance data on multiple computer monitors',
    metrics: [
    { label: 'Time Saved', value: '1,200hrs', icon: 'ClockIcon' },
    { label: 'Automation', value: '85%', icon: 'CogIcon' },
    { label: 'Accuracy', value: '100%', icon: 'CheckCircleIcon' }],

    tags: ['Financial Services', 'Compliance', 'Process Automation'],
    testimonial: {
      quote: 'The compliance automation system has been transformative. What used to take our team weeks now happens automatically with perfect accuracy. It\'s freed up our advisors to focus on what they do best—serving clients.',
      author: 'Robert Williams',
      position: 'Managing Partner, FinanceOne Advisory',
    },
    pdfUrl: 'https://example.com/case-study.pdf'
  },
  {
    id: 'edutech-platform',
    title: 'Learning Platform Scales to 50K Users with 99.9% Uptime',
    client: 'EduTech Innovations',
    industry: 'Technology & SaaS',
    companySize: 'Growth-Stage ($1M-$10M)',
    serviceType: 'Digital Transformation',
    description: 'Built scalable cloud-based learning management system with AI-powered personalization, real-time analytics, and seamless integration capabilities that supported rapid user growth while maintaining exceptional performance.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16d0c03c4-1764661565834.png",
    imageAlt: 'Students collaborating on laptops in modern educational technology lab',
    metrics: [
    { label: 'User Growth', value: '50K+', icon: 'UserGroupIcon' },
    { label: 'Uptime', value: '99.9%', icon: 'ServerIcon' },
    { label: 'Engagement', value: '+180%', icon: 'ChartBarIcon' }],

    tags: ['EdTech', 'Cloud Infrastructure', 'AI Personalization'],
    testimonial: {
      quote: 'CIS Solutions built us a platform that not only met our current needs but was designed to scale with our growth. The AI-powered personalization features have significantly improved student engagement and outcomes.',
      author: 'Amanda Foster',
      position: 'Founder & CEO, EduTech Innovations',
    },
    videoUrl: 'https://example.com/video',
    pdfUrl: 'https://example.com/case-study.pdf'
  },
  {
    id: 'wellness-app',
    title: 'Wellness App Achieves 4.9 Star Rating with 100K Downloads',
    client: 'MindBody Wellness',
    industry: 'Healthcare & Wellness',
    companySize: 'Startup (<$1M)',
    serviceType: 'Marketing Strategy',
    description: 'Launched comprehensive go-to-market strategy combining app store optimization, influencer partnerships, and content marketing that drove organic growth and established strong brand presence in competitive wellness market.',
    image: "https://images.unsplash.com/photo-1504999968522-8765b15c8aee",
    imageAlt: 'Person using fitness app on smartphone while exercising outdoors',
    metrics: [
    { label: 'Downloads', value: '100K+', icon: 'ArrowDownTrayIcon' },
    { label: 'Rating', value: '4.9★', icon: 'StarIcon' },
    { label: 'Retention', value: '78%', icon: 'UserGroupIcon' }],

    tags: ['Mobile App', 'App Marketing', 'User Acquisition'],
    testimonial: {
      quote: 'The marketing strategy CIS Solutions developed was instrumental in our successful launch. Their data-driven approach to user acquisition and retention helped us build a loyal community of engaged users.',
      author: 'David Park',
      position: 'Co-Founder, MindBody Wellness',
    },
    pdfUrl: 'https://example.com/case-study.pdf'
  },
  {
    id: 'manufacturing-iot',
    title: 'IoT Integration Reduces Downtime by 60% in Manufacturing',
    client: 'PrecisionTech Manufacturing',
    industry: 'Manufacturing & Industrial',
    companySize: 'Enterprise ($50M+)',
    serviceType: 'AI Implementation',
    description: 'Deployed IoT sensor network with predictive maintenance AI that monitors equipment health in real-time, predicts failures before they occur, and optimizes production schedules for maximum efficiency.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1111c434b-1766488666884.png",
    imageAlt: 'Industrial manufacturing facility with automated machinery and digital monitoring systems',
    metrics: [
    { label: 'Downtime', value: '-60%', icon: 'ArrowTrendingDownIcon' },
    { label: 'Efficiency', value: '+45%', icon: 'ChartBarIcon' },
    { label: 'Cost Savings', value: '$1.8M', icon: 'CurrencyDollarIcon' }],

    tags: ['IoT', 'Predictive Maintenance', 'Manufacturing'],
    testimonial: {
      quote: 'The predictive maintenance system has transformed our operations. We\'re catching issues before they become problems, and our production efficiency has improved dramatically. The ROI was evident within the first quarter.',
      author: 'James Anderson',
      position: 'Operations Director, PrecisionTech Manufacturing',
    },
    videoUrl: 'https://example.com/video',
    pdfUrl: 'https://example.com/case-study.pdf'
  },
  {
    id: 'realestate-crm',
    title: 'Real Estate CRM Increases Agent Productivity by 85%',
    client: 'Premier Properties Group',
    industry: 'Real Estate',
    companySize: 'Mid-Market ($10M-$50M)',
    serviceType: 'Business Process Optimization',
    description: 'Custom CRM solution with automated lead distribution, AI-powered property matching, and integrated marketing tools that streamlined agent workflows and dramatically improved client service quality.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18bd05316-1764663839206.png",
    imageAlt: 'Real estate agent showing property details on tablet to clients',
    metrics: [
    { label: 'Productivity', value: '+85%', icon: 'ChartBarIcon' },
    { label: 'Response Time', value: '-75%', icon: 'ClockIcon' },
    { label: 'Deals Closed', value: '+120%', icon: 'HomeIcon' }],

    tags: ['CRM', 'Real Estate', 'Workflow Automation'],
    testimonial: {
      quote: 'This CRM system has revolutionized how our agents work. The automated lead distribution and property matching features mean our agents can focus on building relationships instead of administrative tasks.',
      author: 'Lisa Rodriguez',
      position: 'Broker/Owner, Premier Properties Group',
    },
    pdfUrl: 'https://example.com/case-study.pdf'
  },
  {
    id: 'restaurant-ordering',
    title: 'Online Ordering System Drives 200% Revenue Growth',
    client: 'Urban Eats Restaurant Group',
    industry: 'Food & Hospitality',
    companySize: 'Growth-Stage ($1M-$10M)',
    serviceType: 'Digital Transformation',
    description: 'Developed integrated online ordering platform with mobile app, loyalty program, and kitchen management system that transformed customer experience and operational efficiency across multiple locations.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13d06ab79-1764675979502.png",
    imageAlt: 'Modern restaurant interior with customers using tablets for ordering',
    metrics: [
    { label: 'Revenue', value: '+200%', icon: 'CurrencyDollarIcon' },
    { label: 'Orders', value: '3x', icon: 'ShoppingBagIcon' },
    { label: 'Loyalty', value: '65%', icon: 'HeartIcon' }],

    tags: ['Restaurant Tech', 'Mobile App', 'Loyalty Program'],
    testimonial: {
      quote: 'The online ordering system exceeded all our expectations. Not only did it drive significant revenue growth, but the integrated loyalty program has created a community of repeat customers who love our brand.',
      author: 'Marcus Johnson',
      position: 'Owner, Urban Eats Restaurant Group',
    },
    videoUrl: 'https://example.com/video',
    pdfUrl: 'https://example.com/case-study.pdf'
  }];


  const industries: FilterOption[] = [
  { id: 'all', label: 'All Industries', count: caseStudies.length },
  { id: 'Technology & SaaS', label: 'Technology & SaaS', count: caseStudies.filter((cs) => cs.industry === 'Technology & SaaS').length },
  { id: 'Healthcare & Wellness', label: 'Healthcare & Wellness', count: caseStudies.filter((cs) => cs.industry === 'Healthcare & Wellness').length },
  { id: 'E-Commerce & Retail', label: 'E-Commerce & Retail', count: caseStudies.filter((cs) => cs.industry === 'E-Commerce & Retail').length },
  { id: 'Financial Services', label: 'Financial Services', count: caseStudies.filter((cs) => cs.industry === 'Financial Services').length },
  { id: 'Manufacturing & Industrial', label: 'Manufacturing & Industrial', count: caseStudies.filter((cs) => cs.industry === 'Manufacturing & Industrial').length },
  { id: 'Real Estate', label: 'Real Estate', count: caseStudies.filter((cs) => cs.industry === 'Real Estate').length },
  { id: 'Food & Hospitality', label: 'Food & Hospitality', count: caseStudies.filter((cs) => cs.industry === 'Food & Hospitality').length }];


  const companySizes: FilterOption[] = [
  { id: 'all', label: 'All Company Sizes', count: caseStudies.length },
  { id: 'Startup (<$1M)', label: 'Startup (<$1M)', count: caseStudies.filter((cs) => cs.companySize === 'Startup (<$1M)').length },
  { id: 'Growth-Stage ($1M-$10M)', label: 'Growth-Stage ($1M-$10M)', count: caseStudies.filter((cs) => cs.companySize === 'Growth-Stage ($1M-$10M)').length },
  { id: 'Mid-Market ($10M-$50M)', label: 'Mid-Market ($10M-$50M)', count: caseStudies.filter((cs) => cs.companySize === 'Mid-Market ($10M-$50M)').length },
  { id: 'Enterprise ($50M+)', label: 'Enterprise ($50M+)', count: caseStudies.filter((cs) => cs.companySize === 'Enterprise ($50M+)').length }];


  const serviceTypes: FilterOption[] = [
  { id: 'all', label: 'All Services', count: caseStudies.length },
  { id: 'Marketing Automation', label: 'Marketing Automation', count: caseStudies.filter((cs) => cs.serviceType === 'Marketing Automation').length },
  { id: 'Digital Transformation', label: 'Digital Transformation', count: caseStudies.filter((cs) => cs.serviceType === 'Digital Transformation').length },
  { id: 'Marketing Strategy', label: 'Marketing Strategy', count: caseStudies.filter((cs) => cs.serviceType === 'Marketing Strategy').length },
  { id: 'AI Implementation', label: 'AI Implementation', count: caseStudies.filter((cs) => cs.serviceType === 'AI Implementation').length },
  { id: 'Business Process Optimization', label: 'Business Process Optimization', count: caseStudies.filter((cs) => cs.serviceType === 'Business Process Optimization').length }];


  const filteredCaseStudies = useMemo(() => {
    return caseStudies.filter((caseStudy) => {
      const matchesIndustry = selectedIndustry === 'all' || caseStudy.industry === selectedIndustry;
      const matchesCompanySize = selectedCompanySize === 'all' || caseStudy.companySize === selectedCompanySize;
      const matchesServiceType = selectedServiceType === 'all' || caseStudy.serviceType === selectedServiceType;
      const matchesSearch = searchQuery === '' ||
      caseStudy.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      caseStudy.client.toLowerCase().includes(searchQuery.toLowerCase()) ||
      caseStudy.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      caseStudy.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));

      return matchesIndustry && matchesCompanySize && matchesServiceType && matchesSearch;
    });
  }, [selectedIndustry, selectedCompanySize, selectedServiceType, searchQuery, caseStudies]);

  const handleClearFilters = () => {
    setSelectedIndustry('all');
    setSelectedCompanySize('all');
    setSelectedServiceType('all');
    setSearchQuery('');
  };

  const handleLoadMore = () => {
    setDisplayCount((prev) => prev + 6);
  };

  const displayedCaseStudies = filteredCaseStudies.slice(0, displayCount);
  const hasMore = displayCount < filteredCaseStudies.length;

  if (!isHydrated) {
    return (
      <div className="py-16 lg:py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-card rounded-lg shadow-subtle p-6 mb-12">
            <div className="h-8 bg-muted rounded w-48 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) =>
              <div key={i} className="h-12 bg-muted rounded"></div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) =>
            <div key={i} className="bg-card rounded-lg shadow-subtle overflow-hidden">
                <div className="h-64 bg-muted"></div>
                <div className="p-6 space-y-4">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                  <div className="h-20 bg-muted rounded"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>);

  }

  return (
    <div className="py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <FilterBar
          industries={industries}
          companySizes={companySizes}
          serviceTypes={serviceTypes}
          selectedIndustry={selectedIndustry}
          selectedCompanySize={selectedCompanySize}
          selectedServiceType={selectedServiceType}
          searchQuery={searchQuery}
          onIndustryChange={setSelectedIndustry}
          onCompanySizeChange={setSelectedCompanySize}
          onServiceTypeChange={setSelectedServiceType}
          onSearchChange={setSearchQuery}
          onClearFilters={handleClearFilters}
          className="mb-12" />


        {filteredCaseStudies.length === 0 ?
        <div className="text-center py-16">
            <Icon name="FolderOpenIcon" size={64} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-2xl font-display font-bold text-foreground mb-2">No Case Studies Found</h3>
            <p className="text-muted-foreground mb-6">Try adjusting your filters or search query</p>
            <button
            onClick={handleClearFilters}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-md text-sm font-semibold hover:bg-primary/90 transition-all duration-300">

              Clear All Filters
            </button>
          </div> :

        <>
            <div className="mb-6 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {displayedCaseStudies.length} of {filteredCaseStudies.length} case studies
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {displayedCaseStudies.map((caseStudy) =>
            <CaseStudyCard key={caseStudy.id} caseStudy={caseStudy} />
            )}
            </div>

            {hasMore &&
          <div className="text-center">
                <button
              onClick={handleLoadMore}
              className="px-8 py-3 bg-primary text-primary-foreground rounded-md text-base font-semibold hover:bg-primary/90 transition-all duration-300 shadow-subtle inline-flex items-center space-x-2">

                  <span>Load More Case Studies</span>
                  <Icon name="ArrowDownIcon" size={20} />
                </button>
              </div>
          }
          </>
        }
      </div>
    </div>);

};

export default PortfolioInteractive;